/**
 * Dashboard shell (#92): owns the module outlet and the mount-once,
 * keep-alive lifecycle. Modules are hidden when left, never destroyed.
 * See docs/dashboard-shell-design.md, "Lifecycle" and "Boot integration".
 */

import { logger } from '../../core/logger';
import { escapeHtml } from '../../core/utilities/dom';
import { resolveRoute } from './resolve-route';
import type { AppModule, RouteParams } from './module.types';
import type { Router } from './router';

export interface ShellOptions {
  outlet: HTMLElement;
  modules: readonly AppModule[];
  router: Router;
  t: (key: string) => string;
  /** Runs after a module's markup is mounted, e.g. to translate data-i18n. */
  afterMount?: (container: HTMLElement) => void;
}

interface ActiveModule {
  module: AppModule;
  path: string;
  params: RouteParams;
  entered: boolean;
}

export class Shell {
  private readonly options: ShellOptions;
  private readonly containers = new Map<string, HTMLElement>();
  private readonly mounted = new Set<string>();
  private readonly mounting = new Map<string, Promise<boolean>>();
  private active: ActiveModule | null = null;
  private generation = 0;
  private detach: (() => void) | null = null;
  private resolveFirstShown: (() => void) | null = null;
  private readonly firstShown: Promise<void>;

  constructor(options: ShellOptions) {
    this.options = options;
    this.firstShown = new Promise<void>((resolve) => {
      this.resolveFirstShown = resolve;
    });
  }

  getModules(): readonly AppModule[] {
    return this.options.modules;
  }

  /** Mount a module without showing it (boot-time premount). */
  async premount(id: string): Promise<void> {
    const module = this.options.modules.find((m) => m.id === id);
    if (!module) throw new Error(`Unknown module: ${id}`);
    const mounted = await this.ensureMounted(module);
    if (!mounted) throw new Error(`Module "${id}" failed to mount`);
  }

  /** Follow route changes. Resolves once the first route is shown. */
  start(): Promise<void> {
    if (!this.detach) {
      this.detach = this.options.router.onRouteChange((path) => {
        void this.show(path);
      });
      this.options.router.start();
    }
    return this.firstShown;
  }

  navigate(path: string): void {
    this.options.router.navigate(path);
  }

  private async show(path: string | null): Promise<void> {
    const resolution = resolveRoute(path, this.options.modules);
    if (resolution.kind === 'redirect') {
      // The resulting hashchange drives the next show().
      this.options.router.replace(resolution.to);
      return;
    }

    if (this.active?.path === resolution.path) return;

    const generation = ++this.generation;
    const { module } = resolution;
    const previous = this.active;

    if (previous && previous.module !== module) {
      if (previous.entered) this.leave(previous);
      this.containerFor(previous.module).hidden = true;
    }

    const next: ActiveModule = {
      module,
      path: resolution.path,
      params: resolution.params,
      entered: false,
    };
    this.active = next;
    this.containerFor(module).hidden = false;

    const mounted = await this.ensureMounted(module);
    if (generation !== this.generation) return; // a newer navigation took over

    if (mounted) this.enter(next);
    this.markFirstShown();
  }

  private enter(active: ActiveModule): void {
    if (active.entered) return;
    active.entered = true;
    try {
      active.module.onEnter?.(active.params);
    } catch (error) {
      logger.error(`Module "${active.module.id}" onEnter failed:`, error);
    }
  }

  private leave(active: ActiveModule): void {
    try {
      active.module.onLeave?.();
    } catch (error) {
      logger.error(`Module "${active.module.id}" onLeave failed:`, error);
    }
  }

  private ensureMounted(module: AppModule): Promise<boolean> {
    if (this.mounted.has(module.id)) return Promise.resolve(true);

    const inFlight = this.mounting.get(module.id);
    if (inFlight) return inFlight;

    const container = this.containerFor(module);
    // Defer to a microtask so the in-flight entry exists before mount runs.
    const attempt = Promise.resolve()
      .then(() => module.mount(container))
      .then(
        () => {
          this.mounted.add(module.id);
          this.options.afterMount?.(container);
          return true;
        },
        (error: unknown) => {
          logger.error(`Module "${module.id}" failed to mount:`, error);
          this.renderMountError(module, container);
          return false;
        }
      )
      .finally(() => {
        this.mounting.delete(module.id);
      });

    this.mounting.set(module.id, attempt);
    return attempt;
  }

  private renderMountError(module: AppModule, container: HTMLElement): void {
    const { t } = this.options;
    container.innerHTML = `
      <div class="app-module__error" role="alert">
        <p>${escapeHtml(t('shell.mountError'))}</p>
        <button type="button" class="app-module__retry">${escapeHtml(t('shell.retry'))}</button>
      </div>`;
    container
      .querySelector('.app-module__retry')
      ?.addEventListener('click', () => {
        void this.retry(module);
      });
  }

  private async retry(module: AppModule): Promise<void> {
    this.containerFor(module).innerHTML = '';
    const mounted = await this.ensureMounted(module);
    const active = this.active;
    if (mounted && active && active.module === module && !active.entered) {
      this.enter(active);
    }
  }

  private containerFor(module: AppModule): HTMLElement {
    let container = this.containers.get(module.id);
    if (!container) {
      container = document.createElement('section');
      container.className = 'app-module';
      container.dataset.module = module.id;
      container.hidden = true;
      this.options.outlet.appendChild(container);
      this.containers.set(module.id, container);
    }
    return container;
  }

  private markFirstShown(): void {
    this.resolveFirstShown?.();
    this.resolveFirstShown = null;
  }
}
